import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { config } from "config/config";

export const subAdminSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: config.backendPath,
    credentials: "include",
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["SubAdmins", "Editors"],
  endpoints: (builder) => ({
    // ✅ Super Admin Login
    loginSuperAdmin: builder.mutation({
      query: (credentials) => ({
        url: "/api/admin/login",
        method: "POST",
        body: credentials,
      }),
    }),

    // ✅ Sub Admin Login
    loginSubAdmin: builder.mutation({
      query: (credentials) => ({
        url: "/api/subAdmin/login", // <-- make sure backend has this route
        method: "POST",
        body: credentials,
      }),
    }),

    // ✅ Sub-Admin APIs
    createSubAdmin: builder.mutation({
      query: (data) => ({
        url: "/api/subAdmin/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SubAdmins"],
    }),

    getSubAdmins: builder.query({
      query: () => "/api/subAdmin/get",
      providesTags: ["SubAdmins"],
    }),

    getSubAdminById: builder.query({
      query: (id) => `/api/subAdmin/get/${id}`,
      providesTags: ["SubAdmins"],
    }),

    updateSubAdmin: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/subAdmin/update/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["SubAdmins"],
    }),

    deleteSubAdmin: builder.mutation({
      query: (id) => ({
        url: `/api/subAdmin/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SubAdmins"],
    }),

    // ✅ Roles APIs
    getRoles: builder.query({
      query: () => "/api/roles",
      providesTags: ["SubAdmins"],
    }),
    createRole: builder.mutation({
      query: (data) => ({
        url: "/api/roles",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SubAdmins"],
    }),
    updateRole: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/roles/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["SubAdmins"],
    }),
    deleteRole: builder.mutation({
      query: (id) => ({
        url: `/api/roles/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SubAdmins"],
    }),

    // ✅ Editor APIs (per Sub-Admin)
    getEditors: builder.query({
      query: (subAdminId) => `/api/subAdmin/${subAdminId}/editors`,
      providesTags: (result, error, subAdminId) =>
        result
          ? [
              ...result.map((editor) => ({ type: "Editors", id: editor.id })),
              { type: "Editors", id: `LIST-${subAdminId}` },
            ]
          : [{ type: "Editors", id: `LIST-${subAdminId}` }],
    }),

    createEditor: builder.mutation({
      query: ({ subAdminId, ...data }) => ({
        url: `/api/subAdmin/${subAdminId}/editors`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { subAdminId }) => [
        { type: "Editors", id: `LIST-${subAdminId}` },
      ],
    }),

    updateEditor: builder.mutation({
      query: ({ subAdminId, editorId, ...data }) => ({
        url: `/api/subAdmin/${subAdminId}/editors/${editorId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { subAdminId, editorId }) => [
        { type: "Editors", id: editorId },
        { type: "Editors", id: `LIST-${subAdminId}` },
      ],
    }),

    deleteEditor: builder.mutation({
      query: ({ subAdminId, editorId }) => ({
        url: `/api/subAdmin/${subAdminId}/editors/${editorId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { subAdminId, editorId }) => [
        { type: "Editors", id: editorId },
        { type: "Editors", id: `LIST-${subAdminId}` },
      ],
    }),
  }),
});

// ✅ Export hooks
export const {
  useLoginSuperAdminMutation,
  useLoginSubAdminMutation,
  useCreateSubAdminMutation,
  useGetSubAdminsQuery,
  useGetSubAdminByIdQuery,
  useUpdateSubAdminMutation,
  useDeleteSubAdminMutation,
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetEditorsQuery,
  useCreateEditorMutation,
  useUpdateEditorMutation,
  useDeleteEditorMutation,
} = subAdminSlice;
